import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';

import type {
  TabsProps,
  TabListProps,
  TabProps,
  TabPanelProps,
  TabsContextValue,
  TabsVariant,
  TabsSize,
} from './Tabs.types';
import type { KeyboardEvent } from 'react';

// -----------------------------------------------------------------------------
// Context
// -----------------------------------------------------------------------------

const TabsContext = createContext<TabsContextValue | null>(null);

const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tab components must be used within a Tabs component');
  }
  return context;
};

// -----------------------------------------------------------------------------
// Tabs (Container)
// -----------------------------------------------------------------------------

/**
 * Tabs container component that provides context for Tab composition.
 * Supports three visual variants: underline, pill, and segmented.
 */
const Tabs: React.FC<TabsProps> = ({
  items,
  value: controlledValue,
  defaultValue,
  onChange,
  variant = 'underline',
  size = 'md',
  fullWidth = false,
  className = '',
  listClassName,
  children,
  orientation = 'horizontal',
}) => {
  // Track registered tab values for keyboard navigation
  const tabValuesRef = useRef<Set<string>>(new Set());

  // Support controlled and uncontrolled modes
  const [internalValue, setInternalValue] = useState(defaultValue || '');
  const isControlled = controlledValue !== undefined;
  const activeValue = isControlled ? controlledValue : internalValue;

  const setActiveValue = useCallback(
    (newValue: string) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    },
    [isControlled, onChange]
  );

  const registerTab = useCallback((tabValue: string) => {
    tabValuesRef.current.add(tabValue);
  }, []);

  const unregisterTab = useCallback((tabValue: string) => {
    tabValuesRef.current.delete(tabValue);
  }, []);

  const getTabValues = useCallback(() => {
    return Array.from(tabValuesRef.current);
  }, []);

  const contextValue: TabsContextValue = {
    activeValue,
    setActiveValue,
    variant,
    size,
    fullWidth,
    orientation,
    registerTab,
    unregisterTab,
    getTabValues,
  };

  // Set initial value if not provided when using items API
  useEffect(() => {
    if (items && items.length > 0 && !activeValue) {
      const firstEnabled = items.find((item) => !item.disabled);
      if (firstEnabled) {
        setActiveValue(firstEnabled.id);
      }
    }
  }, [items, activeValue, setActiveValue]);

  // If items are provided, render tabs automatically
  if (items && items.length > 0) {
    return (
      <TabsContext.Provider value={contextValue}>
        <div
          className={`${orientation === 'vertical' ? 'flex' : ''} ${className}`}
          data-orientation={orientation}
        >
          <TabList {...(listClassName ? { className: listClassName } : {})}>
            {items.map((item) => (
              <Tab
                key={item.id}
                value={item.id}
                disabled={item.disabled!}
                icon={item.icon}
                badge={item.badge!}
              >
                {item.label}
              </Tab>
            ))}
          </TabList>
          {children}
        </div>
      </TabsContext.Provider>
    );
  }

  // Composition pattern - render children directly
  return (
    <TabsContext.Provider value={contextValue}>
      <div
        className={`${orientation === 'vertical' ? 'flex' : ''} ${className}`}
        data-orientation={orientation}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
};

// -----------------------------------------------------------------------------
// TabList
// -----------------------------------------------------------------------------

/**
 * Container for Tab components. Handles styling based on variant.
 */
const TabList: React.FC<TabListProps> = ({
  children,
  className = '',
  'aria-label': ariaLabel,
}) => {
  const { variant, size, fullWidth, orientation } = useTabsContext();

  // Base container styles
  const baseClasses = 'flex';

  // Orientation classes
  const orientationClasses =
    orientation === 'vertical' ? 'flex-col' : 'flex-row';

  // Variant-specific container styles
  const variantContainerClasses: Record<TabsVariant, string> = {
    underline:
      orientation === 'horizontal'
        ? 'border-b border-stroke-default'
        : 'border-r border-stroke-default',
    pill: 'gap-1 p-1 bg-contrast-700 rounded-lg',
    segmented: 'p-1 bg-contrast-700 rounded-lg',
  };

  // Size-based gap (for underline variant)
  const sizeGapClasses: Record<TabsSize, string> = {
    xs: '', // No gap for compact modal tabs
    sm: variant === 'underline' ? 'gap-4' : '',
    md: variant === 'underline' ? 'gap-6' : '',
    lg: variant === 'underline' ? 'gap-8' : '',
  };

  // Full width handling
  const widthClasses = fullWidth ? 'w-full' : 'inline-flex';

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation={orientation}
      className={[
        baseClasses,
        orientationClasses,
        variantContainerClasses[variant],
        sizeGapClasses[size],
        widthClasses,
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Tab
// -----------------------------------------------------------------------------

/**
 * Individual tab button component.
 * Styling varies based on the parent Tabs variant.
 */
const Tab: React.FC<TabProps> = ({
  value,
  children,
  disabled = false,
  icon,
  badge,
  className = '',
}) => {
  const {
    activeValue,
    setActiveValue,
    variant,
    size,
    fullWidth,
    orientation,
    registerTab,
    unregisterTab,
    getTabValues,
  } = useTabsContext();

  const tabRef = useRef<HTMLButtonElement>(null);
  const isActive = activeValue === value;

  // Register/unregister this tab
  useEffect(() => {
    if (!disabled) {
      registerTab(value);
    }
    return () => unregisterTab(value);
  }, [value, disabled, registerTab, unregisterTab]);

  const handleClick = () => {
    if (!disabled) {
      setActiveValue(value);
    }
  };

  // Keyboard navigation
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const tabValues = getTabValues();
    const currentIndex = tabValues.indexOf(value);

    let newIndex: number | null = null;

    const isHorizontal = orientation === 'horizontal';

    switch (event.key) {
      case 'ArrowRight':
        if (isHorizontal) {
          event.preventDefault();
          newIndex = currentIndex < tabValues.length - 1 ? currentIndex + 1 : 0;
        }
        break;
      case 'ArrowLeft':
        if (isHorizontal) {
          event.preventDefault();
          newIndex = currentIndex > 0 ? currentIndex - 1 : tabValues.length - 1;
        }
        break;
      case 'ArrowDown':
        if (!isHorizontal) {
          event.preventDefault();
          newIndex = currentIndex < tabValues.length - 1 ? currentIndex + 1 : 0;
        }
        break;
      case 'ArrowUp':
        if (!isHorizontal) {
          event.preventDefault();
          newIndex = currentIndex > 0 ? currentIndex - 1 : tabValues.length - 1;
        }
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = tabValues.length - 1;
        break;
    }

    if (newIndex !== null) {
      const newValue = tabValues[newIndex];
      setActiveValue(newValue!);
      // Focus the new tab
      const tabList = tabRef.current?.closest('[role="tablist"]');
      const newTab = tabList?.querySelector(
        `[data-value="${newValue}"]`
      ) as HTMLElement;
      newTab?.focus();
    }
  };

  // Size classes
  const sizeClasses: Record<TabsSize, { text: string; padding: string }> = {
    xs: {
      text: 'text-label-xs tracking-widest font-medium',
      padding: 'px-3 py-2',
    },
    sm: { text: 'text-label-sm', padding: 'px-3 py-1.5' },
    md: { text: 'text-label-sm', padding: 'px-4 py-2' },
    lg: { text: 'text-body-md', padding: 'px-5 py-2.5' },
  };

  // Badge size classes
  //
  // Deliberately NOT `CountBadge`: only the `sm`/`md` steps and the
  // non-pill active fill (`bg-sakura-shade text-primary-300`, i.e. primary
  // soft/strong) have primitive equivalents. `xs` needs a 9px step the type
  // scale doesn't carry, `lg` a 22px min-width, the pill variant's active
  // state is `bg-lift-3 text-neutral-50` which no variant expresses, and
  // `badge` accepts strings as well as numbers. Routing two of four sizes
  // through the primitive would fork the DOM per size/variant, so the whole
  // pill stays hand-rolled until Badge can express all of it.
  const badgeSizeClasses: Record<TabsSize, string> = {
    xs: 'text-label-xs px-1 py-0.5 min-w-4',
    sm: 'text-label-xs px-1.5 py-0.5 min-w-[18px]',
    md: 'text-label-xs px-2 py-0.5 min-w-5',
    lg: 'text-label-sm px-2 py-0.5 min-w-[22px]',
  };

  // Icon size classes
  const iconSizeClasses: Record<TabsSize, string> = {
    xs: 'w-3.5 h-3.5',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-5 h-5',
  };

  // Variant-specific tab styles
  const getVariantClasses = (): string => {
    const base = [
      'relative font-medium transition-all duration-normal',
      'focus:outline-none focus-visible:ring-state-focus',
      sizeClasses[size].text,
      fullWidth ? 'flex-1' : '',
    ];

    if (disabled) {
      base.push('opacity-50 cursor-not-allowed');
    } else {
      base.push('cursor-pointer');
    }

    switch (variant) {
      case 'underline':
        return [
          ...base,
          sizeClasses[size].padding,
          isActive ? 'text-primary-300 font-semibold' : 'text-neutral-500',
          !disabled && !isActive && 'hover:text-neutral-300',
          // Bottom border indicator
          orientation === 'horizontal'
            ? isActive
              ? 'border-b-2 border-primary-300 -mb-px'
              : 'border-b-2 border-transparent -mb-px'
            : isActive
              ? 'border-r-2 border-primary-300 -mr-px'
              : 'border-r-2 border-transparent -mr-px',
        ].join(' ');

      case 'pill':
        return [
          ...base,
          sizeClasses[size].padding,
          'rounded-md',
          isActive
            ? 'bg-sakura-wash border border-stroke-accent text-primary-300'
            : 'text-neutral-400',
          !disabled && !isActive && 'hover:bg-lift-3 hover:text-neutral-100',
        ].join(' ');

      case 'segmented':
        return [
          ...base,
          sizeClasses[size].padding,
          'rounded-md',
          // Field over the card-tone track: the selected segment has to read
          // as lifted off it (mobile's SEGMENTED_ACTIVE is surface.field too).
          isActive ? 'bg-contrast-600 text-neutral-50' : 'text-neutral-400',
          !disabled && !isActive && 'hover:text-neutral-200',
        ].join(' ');

      default:
        return base.join(' ');
    }
  };

  return (
    <button
      ref={tabRef}
      type="button"
      role="tab"
      aria-selected={isActive}
      aria-disabled={disabled}
      tabIndex={isActive ? 0 : -1}
      data-value={value}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={`${getVariantClasses()} ${className}`}
    >
      <span className="flex items-center justify-center gap-2">
        {icon && <span className={iconSizeClasses[size]}>{icon}</span>}
        <span>{children}</span>
        {badge !== undefined && (
          <span
            className={`inline-flex items-center justify-center rounded-full font-medium ${badgeSizeClasses[size]} ${
              isActive
                ? variant === 'pill'
                  ? 'bg-lift-3 text-neutral-50'
                  : 'bg-sakura-shade text-primary-300'
                : 'bg-contrast-700 text-neutral-400'
            }`}
          >
            {typeof badge === 'number' && badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
    </button>
  );
};

// -----------------------------------------------------------------------------
// TabPanel
// -----------------------------------------------------------------------------

/**
 * Content panel for a tab. Only visible when corresponding tab is active.
 */
const TabPanel: React.FC<TabPanelProps> = ({
  value,
  children,
  className = '',
  keepMounted = false,
}) => {
  const { activeValue } = useTabsContext();
  const isActive = activeValue === value;

  // If not keeping mounted and not active, don't render
  if (!keepMounted && !isActive) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      aria-hidden={!isActive}
      tabIndex={isActive ? 0 : -1}
      className={`${isActive ? '' : 'hidden'} ${className}`}
    >
      {children}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Convenience Components
// -----------------------------------------------------------------------------

/**
 * Underline tabs - shorthand for Tabs with variant="underline"
 */
const UnderlineTabs: React.FC<Omit<TabsProps, 'variant'>> = (props) => (
  <Tabs {...props} variant="underline" />
);

/**
 * Pill tabs - shorthand for Tabs with variant="pill"
 */
const PillTabs: React.FC<Omit<TabsProps, 'variant'>> = (props) => (
  <Tabs {...props} variant="pill" />
);

/**
 * Segmented control - shorthand for Tabs with variant="segmented"
 */
const SegmentedControl: React.FC<Omit<TabsProps, 'variant'>> = (props) => (
  <Tabs {...props} variant="segmented" />
);

// -----------------------------------------------------------------------------
// Exports
// -----------------------------------------------------------------------------

export {
  Tabs,
  TabList,
  Tab,
  TabPanel,
  UnderlineTabs,
  PillTabs,
  SegmentedControl,
  useTabsContext,
};

export default Tabs;
